import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Comissao } from '@pgcustom/shared-types';
import type { PgTable, PgTableColumn, PgTableBadgeCell, PgChart } from '@pgcustom/design-system';
import type { Resultado } from '../../core/resultado.js';
import { logger } from '../../core/logger.js';
import type { ComissoesApi } from '../comissoes/comissoes.api.js';
import type { RelatoriosApi } from '../relatorios/relatorios.api.js';

const STATUS_BADGE: Record<Comissao['status'], PgTableBadgeCell> = {
  pendente: { texto: 'Pendente', tom: 'alerta' },
  paga: { texto: 'Paga', tom: 'sucesso' },
  estornada: { texto: 'Estornada', tom: 'erro' },
};

/** Meses de histórico exibidos no gráfico — mesma lista de opções de `dashboard.page.ts`, aplicada aqui a um recorte client-side das próprias comissões (nenhum novo endpoint — `/comissoes/me` já devolve tudo). */
const OPCOES_PERIODO = [3, 6, 12] as const;

const ROTULO_TIPO: Record<Comissao['tipo'], string> = {
  parceiro: 'Venda (cupom)',
  cupom_atribuicao: 'Atribuição por cupom',
  colaborador_global: 'Comissão global',
  assinatura_pg_art: 'Assinatura PG Art',
  tecnico: 'Serviço técnico',
};

/**
 * `<erp-dashboard-colaborador-page>` — visão do Colaborador
 * (referência visual "fase de finalização", perfil Fleck/Marketing).
 * Vive dentro do `<erp-shell>` existente.
 *
 * A comissão exibida é sempre a REAL, lida de `comissoes.regras`
 * (`colaborador_global`/`cupom_atribuicao`) via `/comissoes/me` — nunca
 * assume que "todo colaborador recebe a mesma porcentagem" (a
 * referência mostrava "3% do lucro líquido do site" como texto de
 * exemplo; a regra implementada é 3% sobre o VALOR TOTAL da venda, não
 * sobre lucro líquido — mostramos o valor real ganho, não uma fórmula
 * inventada). Métricas de marketing (CAC, ROAS, leads, campanhas) não
 * têm nenhuma origem de dado real no sistema — mostradas como
 * "não implementado".
 */
export class ErpDashboardColaboradorPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _comissoesApi: ComissoesApi | null = null;
  private _relatoriosApi: RelatoriosApi | null = null;
  private _nomeUsuario = '';
  private periodoMeses: (typeof OPCOES_PERIODO)[number] = 6;
  private minhasComissoes: Comissao[] = [];

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set comissoesApi(valor: ComissoesApi) {
    this._comissoesApi = valor;
  }

  set relatoriosApi(valor: RelatoriosApi) {
    this._relatoriosApi = valor;
  }

  set nomeUsuario(valor: string) {
    this._nomeUsuario = valor;
  }

  connectedCallback(): void {
    this.render();
    void this.carregarComissoes();
    void this.carregarFaturamento();
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0 0 var(--pg-space-1, 4px);
        }
        h1 span { color: var(--pg-color-roxo, #8b5cf6); }
        .cabecalho { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--pg-space-4, 16px); flex-wrap: wrap; margin-bottom: var(--pg-space-5, 24px); }
        .sub { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: var(--pg-font-size-sm, 0.875rem); margin: 0; }
        .seletor-periodo {
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-primario, #f2f3f5);
          font-family: var(--pg-font-familia-base, system-ui);
          font-size: var(--pg-font-size-sm, 0.875rem);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          cursor: pointer;
        }
        .kpis {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-5, 24px);
        }
        .grafico-principal { margin-bottom: var(--pg-space-5, 24px); }
        .secoes {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: var(--pg-space-4, 16px);
        }
      </style>
      <div class="cabecalho">
        <div>
          <h1><span>Olá</span>, ${escaparHtml(primeiroNome(this._nomeUsuario))}</h1>
          <p class="sub">Acompanhe suas comissões e o desempenho do site.</p>
        </div>
        <select class="seletor-periodo" id="seletor-periodo" title="Período do gráfico de comissões">
          ${OPCOES_PERIODO.map((m) => `<option value="${m}" ${m === this.periodoMeses ? 'selected' : ''}>Últimos ${m} meses</option>`).join('')}
        </select>
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-faturamento-site" label="Faturamento do site (mês)" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-comissao-pendente" label="Comissão pendente" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-comissao-paga" label="Comissão paga" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-pedidos" label="Pedidos pagos (mês)" icone="carrinho" estado="carregando"></pg-kpi-card>
      </div>

      <div class="grafico-principal">
        <pg-card>
          <span slot="header">Evolução da sua comissão</span>
          <div id="secao-grafico-comissoes"><pg-empty-state variant="carregando" titulo="Evolução da sua comissão"></pg-empty-state></div>
        </pg-card>
      </div>

      <div class="secoes">
        <pg-card>
          <span slot="header">Suas comissões</span>
          <div id="secao-comissoes"><pg-empty-state variant="carregando" titulo="Suas comissões"></pg-empty-state></div>
        </pg-card>
        <pg-card>
          <span slot="header">Marketing</span>
          <pg-empty-state
            variant="indisponivel"
            titulo="Ainda não disponível"
            descricao="Métricas de marketing (leads, CAC, ROAS, campanhas) não existem no sistema hoje — nenhum valor foi inventado. O módulo de Automações registra regras de notificação, não analytics de marketing."
          ></pg-empty-state>
        </pg-card>
      </div>
    `;

    this.root.querySelector('#seletor-periodo')?.addEventListener('change', (evento) => {
      const valor = Number((evento.target as HTMLSelectElement).value);
      if (!OPCOES_PERIODO.includes(valor as (typeof OPCOES_PERIODO)[number])) return;
      this.periodoMeses = valor as (typeof OPCOES_PERIODO)[number];
      this.renderGraficoComissoes();
    });
  }

  private async carregarComissoes(): Promise<void> {
    if (!this._comissoesApi) return;
    try {
      const resultado = await this._comissoesApi.minhas();
      if (resultado.status !== 'ok') {
        this.aplicarEstadoKpi('kpi-comissao-pendente', resultado);
        this.aplicarEstadoKpi('kpi-comissao-paga', resultado);
        this.renderSecaoEstado('secao-grafico-comissoes', 'Evolução da sua comissão', resultado);
        if (resultado.status === 'vazio') {
          this.definirKpi('kpi-comissao-pendente', formatBRL(0));
          this.definirKpi('kpi-comissao-paga', formatBRL(0));
          this.renderVazio('secao-comissoes', 'Suas comissões', 'nenhuma comissão gerada para você ainda');
          this.renderVazio('secao-grafico-comissoes', 'Evolução da sua comissão', 'nenhuma comissão gerada para você ainda');
        }
        return;
      }

      this.minhasComissoes = resultado.dado;
      const pendente = resultado.dado.filter((c) => c.status === 'pendente').reduce((s, c) => s + c.valorCentavos, 0);
      const paga = resultado.dado.filter((c) => c.status === 'paga').reduce((s, c) => s + c.valorCentavos, 0);
      this.definirKpi('kpi-comissao-pendente', formatBRL(centavosParaReais(pendente)));
      this.definirKpi('kpi-comissao-paga', formatBRL(centavosParaReais(paga)));
      this.renderGraficoComissoes();

      const body = this.root.querySelector('#secao-comissoes');
      if (body) {
        const colunas: PgTableColumn[] = [
          { key: 'tipo', label: 'Origem' },
          { key: 'valor', label: 'Valor', align: 'right' },
          { key: 'status', label: 'Status' },
          { key: 'criadoEm', label: 'Gerada em' },
        ];
        const linhas = resultado.dado.map((c) => ({
          tipo: ROTULO_TIPO[c.tipo],
          valor: formatBRL(centavosParaReais(c.valorCentavos)),
          status: STATUS_BADGE[c.status],
          criadoEm: new Date(c.criadoEm).toLocaleDateString('pt-BR'),
        }));
        body.innerHTML = '<pg-table></pg-table>';
        const tabela = body.querySelector('pg-table') as PgTable;
        tabela.columns = colunas;
        tabela.rows = linhas;
      }
    } catch (erro) {
      logger.erro('falha ao carregar minhas comissões (dashboard colaborador)', { erro });
      this.aplicarEstadoKpi('kpi-comissao-pendente', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoKpi('kpi-comissao-paga', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /** Gráfico calculado 100% client-side a partir das comissões já buscadas (`minhas()`, uma única chamada) — o seletor de período só recorta/agrupa o que já está em memória, nunca refaz a chamada de rede. */
  private renderGraficoComissoes(): void {
    const body = this.root.querySelector('#secao-grafico-comissoes');
    if (!body) return;
    if (this.minhasComissoes.length === 0) {
      this.renderVazio('secao-grafico-comissoes', 'Evolução da sua comissão', 'nenhuma comissão gerada para você ainda');
      return;
    }

    const desde = new Date();
    desde.setMonth(desde.getMonth() - (this.periodoMeses - 1));
    desde.setDate(1);
    const porMes = new Map<string, number>();
    for (const c of this.minhasComissoes) {
      const data = new Date(c.criadoEm);
      if (data < desde) continue;
      const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
      porMes.set(chave, (porMes.get(chave) ?? 0) + c.valorCentavos);
    }

    if (porMes.size === 0) {
      this.renderVazio('secao-grafico-comissoes', 'Evolução da sua comissão', 'nenhuma comissão no período selecionado');
      return;
    }

    body.innerHTML = '<pg-chart></pg-chart>';
    const chart = body.querySelector('pg-chart') as PgChart;
    chart.type = 'bar';
    chart.series = [{ key: 'comissao', label: 'Comissão' }];
    chart.formatarValor = (centavos: number) => formatBRL(centavosParaReais(centavos));
    chart.points = [...porMes.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([chave, valor]) => ({ label: formatarRotuloPeriodo(chave), values: { comissao: valor } }));
  }

  private async carregarFaturamento(): Promise<void> {
    if (!this._relatoriosApi) return;
    try {
      const resultado = await this._relatoriosApi.obterPulsoDoNegocio();
      if (resultado.status !== 'ok') {
        this.aplicarEstadoKpi('kpi-faturamento-site', resultado);
        this.aplicarEstadoKpi('kpi-pedidos', resultado);
        return;
      }
      this.definirKpi('kpi-faturamento-site', formatBRL(centavosParaReais(resultado.dado.receitaMesCentavos)));
      this.definirKpi('kpi-pedidos', String(resultado.dado.pedidosPagosMes));
    } catch (erro) {
      logger.erro('falha ao carregar faturamento (dashboard colaborador)', { erro });
      this.aplicarEstadoKpi('kpi-faturamento-site', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoKpi('kpi-pedidos', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private renderVazio(id: string, titulo: string, descricao: string): void {
    const body = this.root.querySelector(`#${id}`);
    if (!body) return;
    body.innerHTML = `<pg-empty-state variant="vazio" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private renderSecaoEstado(id: string, titulo: string, resultado: Resultado<unknown>): void {
    const body = this.root.querySelector(`#${id}`);
    if (!body) return;
    const variant = resultado.status === 'sem-permissao' ? 'sem-permissao' : resultado.status === 'erro' ? 'erro' : 'indisponivel';
    body.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(legendaDoResultado(resultado))}"></pg-empty-state>`;
  }

  private definirKpi(id: string, valor: string): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'ok');
    card.setAttribute('valor', valor);
  }

  private aplicarEstadoKpi(id: string, resultado: Resultado<unknown>): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'indisponivel');
    card.setAttribute('dica', legendaDoResultado(resultado));
  }
}

function formatarRotuloPeriodo(periodo: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(periodo);
  if (!m) return periodo;
  const [, ano, mes] = m;
  const data = new Date(Number(ano), Number(mes) - 1, 1);
  return data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('.', '');
}

function primeiroNome(nomeCompleto: string): string {
  return nomeCompleto.trim().split(/\s+/)[0] ?? '';
}

function legendaDoResultado(resultado: Resultado<unknown>): string {
  switch (resultado.status) {
    case 'indisponivel':
      return resultado.motivo;
    case 'sem-permissao':
      return 'você não tem permissão para ver este dado';
    case 'erro':
      return resultado.mensagem;
    default:
      return 'não disponível';
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-dashboard-colaborador-page', ErpDashboardColaboradorPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-dashboard-colaborador-page': ErpDashboardColaboradorPage;
  }
}
